import {
    RtCommodityShippedErrors,
    RtCommodityShippedErrorsAdapter,
    RtvTripErrorAdapter,
} from '@bds/railtrac-models';
import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { RtvTripError } from '@bds/railtrac-models';
import { firstValueFrom, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, take } from 'rxjs/operators';
import { ApiErrorHandlerService } from '@bds/helpers';
import { nameof } from '@bds/core';
import DataSource from 'devextreme/data/data_source';
import ODataStore from 'devextreme/data/odata/store';
import { BdsInternalDataResultWithObject } from '@bds/models';
import CustomStore from 'devextreme/data/custom_store';
import { LoadOptions } from 'devextreme/data';
import { BdsDxOdataParser } from '@bds/data-access';
import { IOdataCollectionResult } from '@bds/odata';

export abstract class RtTripErrorApiServiceOptions {
    apiUrl = `api/TripError/`;
    odataUrl = `odata/TripErrorGrid`;
}

@Injectable({
    providedIn: 'root',
})
export class RtTripErrorService {
    constructor(
        private http: HttpClient,
        private options: RtTripErrorApiServiceOptions,
        private adapter: RtvTripErrorAdapter,
        private adapterCommodityShipped: RtCommodityShippedErrorsAdapter,
        private apiErrorHandler: ApiErrorHandlerService,
        private odataParser: BdsDxOdataParser,
    ) {}

    getTripErrorDataSource(): DataSource {
        return new DataSource({
            store: new ODataStore({
                version: 4,
                url: this.options.odataUrl,
                key: 'ormId',
            }),
            paginate: true,
            pageSize: 20,
        });
    }

    getTripErrorGridDataSource(): DataSource {
        const store = new CustomStore({
            key: 'ormId',
            loadMode: 'processed',
            load: (loadOptions: LoadOptions) => {
                const queryString = this.odataParser.buildODataQueryString(loadOptions);
                return firstValueFrom(
                this.http
                    .post(`${this.options.odataUrl}`, queryString)
                    .pipe(
                        map((data: IOdataCollectionResult<RtvTripError>) => {
                            return { totalCount: data['@odata.count'], data: data.value };
                        }),
                    ),
                );
            },
        });

        return new DataSource(store);
    }

    read(ormId: number): Observable<RtvTripError> {
        return this.readViaOdata(ormId);
    }

    readViaApi(ormId: number): Observable<RtvTripError> {
        return this.http.get<RtvTripError>(`${this.options.apiUrl}/${ormId}`).pipe(
            map((data) => this.adapter.adapt(data)),
            catchError((err) => this.apiErrorHandler.handleError(err as HttpErrorResponse)),
        );
    }

    readViaOdata(ormId: number): Observable<RtvTripError> {
        return this.http
            .get<RtvTripError>(`${this.options.odataUrl}(${ormId})`)
            .pipe(catchError((err) => this.apiErrorHandler.handleError(err as HttpErrorResponse)));
    }

    getCommoditiesShipped(ormIds: number[]): Observable<RtCommodityShippedErrors[]> {
        return this.http
            .post<RtCommodityShippedErrors[]>(
                `${this.options.apiUrl}/CommoditiesShippedErrors`,
                ormIds,
            )
            .pipe(
                map((data) => {
                    if (Array.isArray(data)) {
                        const mappedTripErrors = data?.map((m) =>
                            this.adapterCommodityShipped.adapt(m),
                        );
                        return mappedTripErrors.length > 0 ? mappedTripErrors : [];
                    } else if (data) {
                        return [this.adapterCommodityShipped.adapt(data)];
                    } else {
                        return [];
                    }
                }),
                catchError((err) => this.apiErrorHandler.handleError(err as HttpErrorResponse)),
            );
    }

    findTripErrors(ormIds: number[]): Observable<RtvTripError[]> {
        const ormId = this.adapter.metadata.find(
            (f) => f.client === nameof<RtvTripError>('ormId'),
        )?.server;
        let tripErrorToFilter = '';
        ormIds.forEach((id, i) => {
            tripErrorToFilter =
                tripErrorToFilter + `(ormId eq ${id})` + (ormIds.length > i + 1 ? ` or ` : '');
        });
        const filter = `$filter=(${tripErrorToFilter})`;
        const orderby = `$orderby=${ormId} desc`;
        const query = [filter, orderby].join('&');

        return this.http.get<{ value: RtvTripError[] }>(`${this.options.odataUrl}?${query}`).pipe(
            map((data): RtvTripError[] => {
                if (Array.isArray(data.value)) {
                    const mappedTripErrors = data?.value.map((m) => this.adapter.adapt(m));
                    return mappedTripErrors.length > 0 ? mappedTripErrors : [];
                } else {
                    return [this.adapter.adapt(data.value)];
                }
            }),
            catchError((err) => this.apiErrorHandler.handleError(err as HttpErrorResponse)),
        );
    }

    findLatestTripError(carInit: string, carNo: string): Observable<RtvTripError[]> {
        // Get the server field names for carInit and carNo
        const carInitField =
            this.adapter.metadata.find((f) => f.client === nameof<RtvTripError>('carInit'))
                ?.server || 'carInit';

        const carNoField =
            this.adapter.metadata.find((f) => f.client === nameof<RtvTripError>('carNo'))?.server ||
            'carNo';

        // Build filter for carInit and carNo with top 1
        const filter = `$filter=(${carInitField} eq '${carInit}' and ${carNoField} eq '${carNo}')`;
        const orderby = `$orderby=ormId desc`; // Order by latest first
        const top = `$top=1`; // Get only the latest record
        const query = [filter, orderby, top].join('&');

        return this.http.get<{ value: RtvTripError[] }>(`${this.options.odataUrl}?${query}`).pipe(
            map((data): RtvTripError[] => {
                if (Array.isArray(data.value)) {
                    const mappedTripErrors = data?.value.map((m) => this.adapter.adapt(m));
                    return mappedTripErrors.length > 0 ? mappedTripErrors : [];
                } else if (data.value) {
                    return [this.adapter.adapt(data.value)];
                } else {
                    return [];
                }
            }),
            catchError((err) => this.apiErrorHandler.handleError(err as HttpErrorResponse)),
        );
    }

    update(ormId: number, trip: RtvTripError): Observable<RtvTripError> {
        return this.http
            .put<unknown>(`${this.options.apiUrl}/${ormId}`, this.adapter.toServer(trip))
            .pipe(
                map((data: unknown) => {
                    return data ? this.adapter.adapt(data) : null;
                }),
                shareReplay(),
                catchError((err) => this.apiErrorHandler.handleError(err as HttpErrorResponse)),
            );
    }

    delete(ormId: number): Observable<boolean> {
        const obs = this.http.delete(`${this.options.apiUrl}/${ormId}`).pipe(
            take(1),
            shareReplay(),
            map((m) => !!m),
        );
        obs.subscribe(() => {});
        return obs;
    }

    reProcess(ormIds: number[]): Observable<BdsInternalDataResultWithObject<RtvTripError[]>> {
        const obs = this.http
            .put<BdsInternalDataResultWithObject<RtvTripError[]>>(
                `${this.options.apiUrl}/Reprocess`,
                ormIds,
            )
            .pipe(take(1), shareReplay());
        obs.subscribe();
        return obs;
    }

    addImport(tripError: RtvTripError): Observable<RtvTripError> {
        const obs = this.http.put(`${this.options.apiUrl}/AddImport`, tripError).pipe(
            take(1),
            shareReplay(),
            map((m) => this.adapter.adapt(m)),
        );
        obs.subscribe();
        return obs;
    }

    updateInterfaceValidation(tripError: RtvTripError): Observable<RtvTripError> {
        const obs = this.http.put(`${this.options.apiUrl}/Update`, tripError).pipe(
            take(1),
            shareReplay(),
            map((m) => this.adapter.adapt(m)),
        );
        obs.subscribe();
        return obs;
    }

    deleteErrors(ormIds: number[]): Observable<boolean> {
        const obs = this.http.put(`${this.options.apiUrl}/DeleteTripErrors`, ormIds).pipe(
            take(1),
            shareReplay(),
            map((m) => !!m),
        );
        obs.subscribe();
        return obs;
    }

    readCombined(ormIds: number[]): Observable<{ trip: RtvTripError; keys: unknown[] }> {
        return this.http
            .get<{ tripError: RtvTripError; keys: unknown[] }>(
                `${this.options.apiUrl}/UniqueTripError`,
                {
                    params: {
                        id: ormIds.join(','),
                    },
                },
            )
            .pipe(
                map((data) => {
                    return {
                        trip: this.adapter.adapt(data?.tripError),
                        keys: [...data.keys.map((m) => this.adapter.adapt(m))],
                    };
                }),
                catchError((err) => this.apiErrorHandler.handleError(err as HttpErrorResponse)),
            );
    }
}
